import { AppRoutes } from '../../common/constants';
import { Box } from '@mui/material';
import { DEFAULT_ERROR_MESSAGE } from '../../common/lib/fulcrumApi';
import { FNADetails, FnaSuretyshipParty } from '../../common/types/analyses';
import { GridColDef, GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid';
import { JSX, useEffect, useMemo } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  makeBooleanColumn,
  makeEstateDutyColumns,
  makeNumberColumn,
} from '../../common/helpers/dataGrid';
import { useFnaPartiesUpdater } from '../hooks/useFnaPartiesUpdater';
import { useIsFnaQuoted } from '../hooks/useIsFnaQuoted';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useUpdateFna } from '../../common/components/mutations';
import Content from '../../common/components/content';
import DataGrid from '../../common/components/dataGrid';
import DataGridDeleteAction from '../../common/components/dataGrid/dataGridDeleteAction';
import FnaPageHeaderSummary from '../components/fnaPageHeaderSummary';
import Layout from '../../common/components/layout';
import LayoutLoadingState from '../../common/components/layout/layoutLoadingState';
import StepFooter from '../../common/components/stepFooter';

/**
 * Component to edit "otherDescription" field.
 */
export const EditOtherDescriptionCell = ({
  id,
  value,
  field,
  row,
}: GridRenderEditCellParams): JSX.Element | null => {
  const apiRef = useGridApiContext();

  const handleValueChange = (value: string): void => {
    apiRef.current.setEditCellValue({ id, field, value });
  };

  if ((row.other && row.other !== '0') || (row.riskOther && row.riskOther !== '0')) {
    return (
      <textarea
        autoFocus
        value={value}
        className="w-full h-full"
        onKeyDown={(event): void => {
          event.stopPropagation();
        }}
        onChange={(event): void => handleValueChange(event.target.value)}
      />
    );
  }

  apiRef.current.stopCellEditMode({
    id: id,
    field: field,
    ignoreModifications: true,
  });

  return null;
};

/**
 * Mapping FNA suretyship party field name to display name
 */
const editableSuretyshipFieldNames: Record<string, string> = {
  bonds: 'Bonds',
  overdrafts: 'Overdrafts',
  leases: 'Leases',
  hirepurchases: 'Hire purchases',
  loansLongterm: 'Long-term loans',
  existingprovision: 'Existing provision',
  other: 'Other',
};

const editableSuretyshipBooleanFieldNames: Record<string, string> = {
  disabilityBenefit: 'Disability benefits',
};

/**
 * Suretyship edit form for the FNA
 */
const FnaSuretyshipPage = (): JSX.Element => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { mutateAsync, isPending: mutationIsLoading } = useUpdateFna();
  const { data: fna } = useQuery<FNADetails>({
    queryKey: [`/FNADetail/${id}`],
    enabled: Boolean(id),
  });
  const { processRowUpdate, parties, getRowId, checkIfCellIsEditable, deleteParty } =
    useFnaPartiesUpdater(fna?.suretyshipParties, true);

  const hasEstateDuty = parties.some((party) => party.isEstateduty);

  // Do not allow to edit needs if FNA is quoted (redirect to the FNA page)
  const { isSuretyshipQuoted } = useIsFnaQuoted(Number(id));
  useEffect(() => {
    if (isSuretyshipQuoted) {
      navigate(`${AppRoutes.FNAs}/${id}`);
    }
  }, [isSuretyshipQuoted, navigate, id]);

  const columns = useMemo<GridColDef<FnaSuretyshipParty>[]>(
    (): GridColDef<FnaSuretyshipParty>[] => [
      {
        field: 'actions',
        type: 'actions',
        getActions: ({ row }) => [
          <DataGridDeleteAction key="delete" onClick={(): void => deleteParty(row)} />,
        ],
      },
      {
        field: 'partyRoleplayerName',
        headerName: 'Name',
        minWidth: 150,
      },
      {
        field: 'representativeRoleplayerName',
        headerName: 'Representative life',
        minWidth: 150,
      },
      ...Object.entries(editableSuretyshipFieldNames).map<GridColDef<FnaSuretyshipParty>>(
        ([field, headerName]) =>
          makeNumberColumn({
            field,
            headerName,
          }),
      ),
      {
        field: 'otherDescription',
        headerName: 'Other Description',
        minWidth: 300,
        editable: true,
        renderCell: ({ row }) => (
          <span className="whitespace-pre-line">{row.other ? row.otherDescription : ''}</span>
        ),
        renderEditCell: EditOtherDescriptionCell,
      },
      ...Object.entries(editableSuretyshipBooleanFieldNames).map<GridColDef<FnaSuretyshipParty>>(
        ([field, headerName]) =>
          makeBooleanColumn({
            field,
            headerName,
            processRowUpdate,
          }),
      ),
      ...makeEstateDutyColumns(processRowUpdate, true),
    ],
    [processRowUpdate, deleteParty],
  );

  if (!fna) {
    return <LayoutLoadingState />;
  }

  const fnaRoleplayers =
    fna.roleplayers.map(({ roleplayerName }) => roleplayerName).join(', ') || '-';
  const breadcrumbs = [
    { label: 'Financial needs analyses', href: AppRoutes.FNAs },
    { label: fnaRoleplayers, href: `${AppRoutes.FNAs}/${fna.id}` },
  ];

  const handleSave = async (): Promise<void> => {
    try {
      await mutateAsync({
        ...fna,
        suretyshipParties: parties,
      });
    } catch (error) {
      enqueueSnackbar((error as Error).message || DEFAULT_ERROR_MESSAGE, { variant: 'error' });
    }
  };

  const handleSaveAndExit = async (): Promise<void> => {
    await handleSave();
    navigate(AppRoutes.FNAs);
  };

  const handleCalculation = async (): Promise<void> => {
    if (parties.some((party) => party.other && !party.otherDescription)) {
      enqueueSnackbar('Please enter a description for the "Other" field', { variant: 'error' });
      return;
    }
    await handleSave();
    navigate(`${AppRoutes.FNAs}/${fna.id}/suretyship/needs`);
  };

  const handleClickBack = (): void => {
    navigate(`${AppRoutes.FNAs}/${fna.id}?tab=3`);
  };

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <Content>
        <Box className="md:flex justify-between items-start md:space-x-4 space-y-8 md:space-y-0">
          <FnaPageHeaderSummary
            fna={fna}
            title="Contingent Liability (Suretyship protection) needs"
          />
          <LoadingButton
            loading={mutationIsLoading}
            className="flex-shrink-0"
            variant="contained"
            color="primary"
            onClick={handleSaveAndExit}
          >
            Save and exit
          </LoadingButton>
        </Box>
        <DataGrid
          actionsVisibleOnHover={false}
          className="mt-12"
          rows={parties}
          columns={columns}
          enableQuickFilter={false}
          getRowId={getRowId}
          processRowUpdate={processRowUpdate}
          isCellEditable={checkIfCellIsEditable}
        />
        {hasEstateDuty && (
          <p className="mt-4">
            * The policy proceeds will be subject to estate duty in the estate of the deceased life
            insured. The sum insured should be adjusted to make provision for the estate duty as the
            executor will claim the proportionate estate duty from the policyholder (the business).
            Estate duty is levied at 20% or 25%. To make provision for this, the sum insured must be
            divided by either 0.8 or 0.75.
          </p>
        )}
        <StepFooter
          isLoading={false}
          nextButtonTitle="Calculate cover required"
          onClickNext={handleCalculation}
          onClickBack={handleClickBack}
        />
      </Content>
    </Layout>
  );
};

export default FnaSuretyshipPage;
